import { inject, Injectable } from '@angular/core';
import { GetThemeParams, ITheme, IThemesPreviews } from '../modals/themes/themes.interface';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { BaseService } from './base.service';
import { DEVICE_TYPE } from '../enum/constants';
import { map, Observable } from 'rxjs';
import { colorThemes, defaultColorTheme } from '../modals/themes/themes.constants';
import { IUser } from '../interfaces';

interface ApiResponse<T> {
  data: T;
}

interface SetThemePayload {
  theme_mobile?: string;
  theme_desktop?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly baseService = inject(BaseService);
  private readonly http = inject(HttpClient);

  public isLoader: boolean = false;
  public theme: ITheme | null = null;

  themePreview?: ITheme | null;
  themesPreviews: IThemesPreviews[] = [];
  private isPreview = false;

  getThemesPreviews(): void {
    this.isLoader = true;

    const params = {
      device_type: this.getDeviceType(),
    };
    this.http.get<ApiResponse<IThemesPreviews[]>>(environment.baseURL + '/themes/previews', { params })
      .pipe(map((res) => res.data))
      .subscribe({
        next: (res: IThemesPreviews[]) => {
          this.themesPreviews = this.mergeWithColorThemePreviews(res);
          this.isLoader = false;
        },
        error: () => {
          this.themesPreviews = this.getColorThemePreviews();
          this.isLoader = false;
        },
      });
  }

  getTheme(): void {
    const loadThemeParams = this.getLoadThemeParams();
    const colorTheme = this.getColorTheme(loadThemeParams.name);

    if (this.theme?.name === loadThemeParams.name) return;

    if (colorTheme) {
      this.theme = colorTheme;
      return;
    }

    this.theme = null;
    this.loadTheme(loadThemeParams)
      .subscribe({
        next: (res: ITheme | null) => {
          this.theme = res || defaultColorTheme;
        },
        error: () => {
          this.theme = defaultColorTheme;
        },
      });
  }

  private loadTheme(themeParams: GetThemeParams): Observable<ITheme | null> {
    const params = {
      ...themeParams,
    };

    return this.http.get<ApiResponse<ITheme | null>>(environment.baseURL + '/themes', { params })
      .pipe(map((res) => res.data));
  }

  setTheme(name: string): Observable<unknown> {
    const payload: SetThemePayload = {};
    if (this.baseService.isMobile) {
      payload.theme_mobile = name;
    } else {
      payload.theme_desktop = name;
    }
    return this.http.put(environment.baseURL + '/profile/theme', payload);
  }

  getThemeObj(): ITheme | null {
    if (this.isPreview && this.themePreview) return this.themePreview;

    return this.theme;
  }

  getThemeLayoutStyles(): Record<string, string> {
    const theme = this.getThemeObj();

    if (theme?.colors) {
      return {
        background: theme.colors.background,
        'background-image': 'none',
        color: theme.colors.text,
        '--theme-background': theme.colors.background,
        '--theme-surface': theme.colors.surface,
        '--theme-muted': theme.colors.muted,
        '--theme-text': theme.colors.text,
        '--theme-text-muted': theme.colors.textMuted,
        '--primary-color': theme.colors.accent,
        '--primary-color-rgb': `rgb(${theme.colors.accentRgbCode})`,
        '--primary-color-rgb-code': theme.colors.accentRgbCode,
        '--background': theme.colors.background,
        '--surface': theme.colors.surface,
        '--card-bg': theme.colors.surface,
        '--muted-bg': theme.colors.muted,
        '--border-color': theme.colors.border,
        '--text-color-muted': theme.colors.textMuted,
        '--text-inverse': theme.colors.text,
      };
    }

    if (theme?.img_url) {
      return {
        'background-image': `url(${theme.img_url})`,
      };
    }

    return {};
  }

  getThemePreviewStyles(theme: IThemesPreviews): Record<string, string> {
    if (theme.colors) {
      return {
        'background-color': theme.colors.background,
        'background-image': 'none',
        'border-color': theme.colors.border,
      };
    }

    if (theme.preview_url) {
      return {
        'background-image': `url(${theme.preview_url})`,
      };
    }

    return {};
  }

  getThemePreviewLabelStyles(theme: IThemesPreviews): Record<string, string> {
    if (!theme.colors) return {};

    return {
      background: theme.colors.surface,
      color: theme.colors.text,
      border: `1px solid ${theme.colors.border}`,
    };
  }

  preview(name: string): void {
    this.isPreview = true;
    this.themePreview = null;
    setTimeout(() => {
      const previewObj = this.themesPreviews.find(v => v.name === name);

      if (previewObj) {
        this.themePreview = {
          name: previewObj.name,
          img_url: previewObj.preview_url,
          colors: previewObj.colors,
        } as ITheme;
      }
    });
  }

  reset(): void {
    this.isPreview = false;
    this.themePreview = null;
  }

  private getDeviceType(): DEVICE_TYPE {
    if (this.baseService.isMobile) return DEVICE_TYPE.MOBILE;
    return DEVICE_TYPE.DESKTOP;
  }

  private getLoadThemeParams(): GetThemeParams {
    const currentUserInfo = this.baseService.currentUserInfo as IUser | undefined;
    let currentTheme: string = defaultColorTheme.name;

    if (this.baseService.isMobile) {
      currentTheme = currentUserInfo?.settings?.theme_mobile || defaultColorTheme.name;
    } else {
      currentTheme = currentUserInfo?.settings?.theme_desktop || defaultColorTheme.name;
    }

    return {
      type: this.getDeviceType(),
      name: currentTheme,
    };
  }

  private getColorTheme(name: string): ITheme | null {
    return colorThemes.find(theme => theme.name === name || theme.aliases?.includes(name)) || null;
  }

  private getColorThemePreviews(): IThemesPreviews[] {
    return colorThemes.map(theme => ({
      name: theme.name,
      has_video: false,
      colors: theme.colors,
    }));
  }

  private mergeWithColorThemePreviews(themes: IThemesPreviews[] = []): IThemesPreviews[] {
    const colorThemePreviews = this.getColorThemePreviews();
    const colorThemeNames = new Set(colorThemes.flatMap(theme => [theme.name, ...(theme.aliases || [])]));
    const mediaThemePreviews = themes.filter(theme => !colorThemeNames.has(theme.name));

    return [
      ...colorThemePreviews,
      ...mediaThemePreviews,
    ];
  }

}
